/**
 * ProfileForm — profile identity section of the editor panel.
 *
 * Renders a profile image upload area, full-name input, and bio/subtitle input.
 * Communicates changes via the onChange callback.
 */
export class ProfileForm {
  /**
   * @param {Object} opts
   * @param {Function} opts.onChange - called with { name, subtitle, profileImageFile }
   */
  constructor({ onChange }) {
    this._onChange = onChange;
    this._container = null;
    this._imagePreview = null;
    this._fileInput = null;
    this._nameInput = null;
    this._subtitleInput = null;
    this._displayName = null;
    this._displaySubtitle = null;

    this._currentFile = null;
    this._fileJustSelected = false;
    this._currentName = '';
    this._currentSubtitle = '';
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /** Render the profile form into the given container element. */
  render(container) {
    this._container = container;

    const section = document.createElement('div');
    section.className = 'profile-form';

    // Section label
    const label = document.createElement('div');
    label.className = 'editor-section-label';
    label.textContent = 'Profile Identity';
    section.appendChild(label);

    // Image area
    section.appendChild(this._buildImageArea());

    // Name input
    section.appendChild(this._buildInputGroup('FULL NAME', 'text', 'Your name', 'name'));

    // Subtitle input
    section.appendChild(this._buildInputGroup('BIO / SUBTITLE', 'text', 'e.g. Ultra runner & trail enthusiast', 'subtitle'));

    container.appendChild(section);
  }

  /** Populate form with existing resume data. */
  setData({ name, subtitle, profileImageUrl }) {
    if (this._nameInput) {
      this._nameInput.value = name || '';
      this._currentName = name || '';
    }
    if (this._subtitleInput) {
      this._subtitleInput.value = subtitle || '';
      this._currentSubtitle = subtitle || '';
    }
    if (this._displayName) {
      this._displayName.textContent = name || 'Your Name';
    }
    if (this._displaySubtitle) {
      this._displaySubtitle.textContent = subtitle || 'Bio / Subtitle';
    }
    if (this._imagePreview && profileImageUrl) {
      this._setImagePreview(profileImageUrl);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Internal builders                                                  */
  /* ------------------------------------------------------------------ */

  _buildImageArea() {
    const area = document.createElement('div');
    area.className = 'profile-image-area';

    // Upload trigger
    const upload = document.createElement('div');
    upload.className = 'profile-image-upload';

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png';
    this._fileInput = fileInput;
    upload.appendChild(fileInput);

    const circle = document.createElement('div');
    circle.className = 'profile-image-circle';
    circle.innerHTML = `
      <div class="profile-image-placeholder">
        <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      </div>
    `;
    this._imagePreview = circle;
    upload.appendChild(circle);

    // Camera icon overlay
    const camera = document.createElement('div');
    camera.className = 'profile-image-camera';
    camera.innerHTML = `
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 15.2a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4z"/>
        <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
      </svg>
    `;
    upload.appendChild(camera);

    // Click to open file picker
    upload.addEventListener('click', () => fileInput.click());

    // Handle file selection
    fileInput.addEventListener('change', () => {
      const file = fileInput.files[0];
      if (!file) return;
      this._currentFile = file;
      this._fileJustSelected = true;

      const url = URL.createObjectURL(file);
      this._setImagePreview(url);
      this._emitChange();
    });

    area.appendChild(upload);

    // Info text next to image
    const info = document.createElement('div');
    info.className = 'profile-image-info';

    const displayName = document.createElement('div');
    displayName.className = 'profile-image-name';
    displayName.textContent = 'Your Name';
    this._displayName = displayName;

    const displaySubtitle = document.createElement('div');
    displaySubtitle.className = 'profile-image-subtitle-text';
    displaySubtitle.textContent = 'Bio / Subtitle';
    this._displaySubtitle = displaySubtitle;

    info.appendChild(displayName);
    info.appendChild(displaySubtitle);
    area.appendChild(info);

    return area;
  }

  _buildInputGroup(labelText, type, placeholder, field) {
    const group = document.createElement('div');
    group.className = 'editor-input-group';

    const label = document.createElement('label');
    label.className = 'editor-input-label';
    label.textContent = labelText;
    group.appendChild(label);

    const input = document.createElement('input');
    input.type = type;
    input.className = 'editor-input';
    input.placeholder = placeholder;
    group.appendChild(input);

    if (field === 'name') {
      this._nameInput = input;
      input.addEventListener('input', () => {
        this._currentName = input.value;
        if (this._displayName) {
          this._displayName.textContent = input.value || 'Your Name';
        }
        this._emitChange();
      });
    } else if (field === 'subtitle') {
      this._subtitleInput = input;
      input.addEventListener('input', () => {
        this._currentSubtitle = input.value;
        if (this._displaySubtitle) {
          this._displaySubtitle.textContent = input.value || 'Bio / Subtitle';
        }
        this._emitChange();
      });
    }

    return group;
  }

  _setImagePreview(url) {
    if (!this._imagePreview) return;
    // Use createElement instead of innerHTML to prevent URL injection
    this._imagePreview.innerHTML = '';
    const img = document.createElement('img');
    img.alt = 'Profile';
    // Only allow safe URL protocols
    if (url && (url.startsWith('https://') || url.startsWith('http://') || url.startsWith('blob:'))) {
      img.src = url;
    }
    this._imagePreview.appendChild(img);
  }

  _emitChange() {
    const payload = {
      name: this._currentName,
      subtitle: this._currentSubtitle,
      profileImageFile: this._fileJustSelected ? this._currentFile : null,
    };
    this._fileJustSelected = false;
    this._onChange?.(payload);
  }
}
